/**
 * ===================================================================================
 * 📁 경로: services/frontend/src/pages/ActionPlanLogsPage.jsx
 * 📌 프로그램명: 실천 계획 로그(건강 일기) 관리 페이지
 * 🧩 개요:
 * - 특정 AI 리포트에 속한 모든 실천 계획(Action Plan)에 대한 사용자의 수행 기록(Log)을
 * 관리(조회, 생성, 수정)하는 UI를 제공합니다.
 * - 사용자는 이 페이지를 '건강 일기'처럼 사용하여 자신의 건강 활동을 기록하고 추적할 수 있습니다.
 * - 여기서 수집된 데이터는 AI 피드백 시스템의 강화학습(RLHF)을 위한 핵심 '보상' 데이터로 활용됩니다.
 * ===================================================================================
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ActionPlanLogModal from '../components/ActionPlanLogModal.jsx';
import MessageModal from '../components/MessageModal';

/**
 * [Why]
 * AI가 제안한 건강 관리 계획을 사용자가 실제로 어떻게 수행하고 있는지를 기록하고 시각화하는
 * 페이지입니다. 이는 사용자의 건강 활동에 대한 책임감과 동기를 부여하고, AI 모델이
 * 사용자의 피드백을 학습하여 더 나은 제안을 하도록 만드는 '피드백 루프'의 핵심적인
 * 사용자 인터페이스 역할을 합니다.
 *
 * [How]
 * - **데이터 조회**: 페이지 진입 시 `useEffect`를 통해 `fetchLogs`와 실천 계획 목록을
 * 비동기적으로 호출합니다. `useParams`로 URL에서 현재 `reportId`를 가져와 API 요청에 사용합니다.
 * - **상태 관리**: `useState` 훅을 사용하여 로그 목록(logs), 로딩/에러 상태, 필터 조건,
 * 페이지네이션, 모달 상태 등 페이지의 모든 동적 상태를 관리합니다.
 * - **필터링 및 정렬**: 사용자가 입력한 조회 기간, 플랜 종류에 따라 클라이언트 측에서
 * 1차 필터링을 수행하고, 최신순으로 데이터를 정렬하여 보여줍니다.
 * - **CRUD 인터페이스**: 사용자는 '로그 추가' 버튼으로 새 기록을 생성하거나, 각 로그 옆의
 * '수정' 버튼을 통해 기존 기록을 업데이트할 수 있습니다. 모든 생성/수정 작업은
 * `ActionPlanLogModal` 컴포넌트를 통해 이루어집니다.
 * - **반응형 UI**: 화면 너비에 따라 테이블 레이아웃을 동적으로 변경하여 모바일과 데스크탑
 * 환경 모두에서 최적의 사용성을 제공합니다.
 *
 * @component
 * @returns {JSX.Element} ActionPlanLogsPage 컴포넌트의 JSX 엘리먼트.
 */
export default function ActionPlanLogsPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const [isMobile, setIsMobile] = useState(false);

  // 필터 상태
  const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState('');
  const [source, setSource] = useState(''); // '', 'manual', 'wearable'

  // 데이터 상태
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 로그/계획 조회 오류를 모달창으로 안내하기 위한 상태
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [plans, setPlans] = useState([]); // {plan_id, suggestion}

  // 모달 상태
  const [openModal, setOpenModal] = useState(false);
  const [editLog, setEditLog] = useState(null); // 편집 대상
  const initialPlanIdFromState = (() => {
    const pid = location?.state?.planId;
    return (pid !== undefined && pid !== null) ? String(pid) : '';
  })();
  const [createPlanId, setCreatePlanId] = useState(initialPlanIdFromState);
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlanIdFromState);

  // 클라이언트 사이드 페이지네이션 로직
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalPages = useMemo(() => Math.max(1, Math.ceil((logs?.length || 0) / pageSize)), [logs]);
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (logs || []).slice(start, start + pageSize);
  }, [logs, page]);

  // UI 표시에 사용될 한글 레이블 (메모이제이션으로 불필요한 재생성 방지)
  const statusLabel = useMemo(() => ({
    completed: '완료',
    in_progress: '진행중',
    skipped: '건너뜀',
  }), []);

  // 소스 표기 매핑
  const sourceLabel = useMemo(() => ({
    manual: '직접',
    wearable: '웨어러블',
  }), []);

  // 화면 크기 변화를 감지하여 모바일/데스크탑 레이아웃을 전환
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /**
   * [Why]
   * 서버로부터 특정 리포트에 대한 실천 계획 로그 목록을 비동기적으로 조회합니다.
   * 사용자가 설정한 필터 조건에 맞는 데이터만 가져오는 역할을 합니다.
   *
   * [How]
   * - `axios`를 사용하여 `/api/v1/reports/{reportId}/logs` 엔드포인트에 GET 요청을 보냅니다.
   * - `dateFrom`, `dateTo` 등 상태 값을 기반으로 요청 파라미터를 동적으로 구성합니다.
   * - 응답 데이터를 받아 클라이언트 사이드에서 추가 필터링(source, planId) 및 정렬을 수행한 후,
   * `logs` 상태를 업데이트합니다.
   */
  const fetchLogs = async () => {
    if (!reportId) return;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (dateFrom) params.date_from = `${dateFrom}T00:00:00Z`;
      if (dateTo) params.date_to = `${dateTo}T23:59:59Z`;
      if (selectedPlanId) params.plan_id = Number(selectedPlanId);
      const { data } = await axios.get(`${apiUrl}/api/v1/reports/${reportId}/logs`, { params });
      let out = Array.isArray(data) ? data : [];
      if (source) out = out.filter(l => (l.source || '') === source);
      if (selectedPlanId) out = out.filter(l => String(l.plan_id) === String(selectedPlanId));
      // 최신순 정렬 (performed_at 있으면 우선, 없으면 log_date)
      out.sort((a, b) => new Date(b.performed_at || b.log_date) - new Date(a.performed_at || a.log_date));
      setLogs(out);
      setPage(1);
    } catch (e) {
      const backendDetail = e?.response?.data?.detail || e?.response?.data?.message;
      const msg = backendDetail || e?.message || '기록을 불러오지 못했습니다.';
      setError(msg);
      setIsErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // reportId나 selectedPlanId가 변경되면 로그 목록을 새로고침
  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, selectedPlanId]);

  // 페이지 진입 시, 필터 드롭다운을 채우기 위해 전체 실천 계획 목록을 조회
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${apiUrl}/api/v1/reports/${reportId}`);
        const goals = Array.isArray(data?.health_goals) ? data.health_goals : [];
        const planList = [];
        goals.forEach(g => (g.action_plans || []).forEach(p => {
          if (p?.plan_id) planList.push({ plan_id: p.plan_id, suggestion: p.suggestion || '실천 항목' });
        }));
        if (mounted) setPlans(planList);
      } catch (e) {
        const backendDetail = e?.response?.data?.detail || e?.response?.data?.message;
        const msg = backendDetail || e?.message || '실천 계획 목록을 불러오지 못했습니다.';
        if (mounted) {
          setError(msg);
          setIsErrorModalOpen(true);
          setPlans([]);
        }
      }
    })();
    return () => { mounted = false; };
  }, [apiUrl, reportId]);

  // 네비게이션 state가 바뀔 때 동기화 (새로고침 없이 탭 이동 등)
  useEffect(() => {
    const pid = location?.state?.planId;
    if (pid !== undefined && pid !== null) {
      setCreatePlanId(String(pid));
      setSelectedPlanId(String(pid));
    }
  }, [location?.state?.planId]);

  const onOpenCreate = () => {
    if (!(createPlanId || selectedPlanId)) {
      alert('추가할 플랜 ID를 입력하세요.');
      return;
    }
    setEditLog(null);
    setOpenModal(true);
  };

  const onOpenEdit = (log) => {
    setEditLog(log);
    setOpenModal(true);
  };

  const onSubmitted = () => {
    setOpenModal(false);
    setEditLog(null);
    fetchLogs();
  };

  return (
    <div style={{ padding: 16, maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>나의 건강 일기 : 실천 기록 관리</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              height: 32, padding: '6px 10px', borderRadius: 999,
              border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)', cursor: 'pointer',
              fontSize: 14, lineHeight: 1
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            뒤로
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 6 : 8, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>시작일</div>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              width: isMobile ? 128 : '100%',
              padding: isMobile ? '3px 6px' : '8px 10px',
              fontSize: 16,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              lineHeight: 1.2,
              boxSizing: 'border-box',
              alignSelf: 'start',
            }}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>종료일</div>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              width: isMobile ? 128 : '100%',
              padding: isMobile ? '3px 6px' : '8px 10px',
              fontSize: 16,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              lineHeight: 1.2,
              boxSizing: 'border-box',
              alignSelf: 'start',
            }}
          />
        </div>
        {!isMobile && (
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>입력</div>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8 }}
            >
              <option value="">전체</option>
              <option value="manual">직접</option>
              <option value="wearable">웨어러블</option>
            </select>
          </div>
        )}
        <div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>플랜</div>
          <select
            value={selectedPlanId}
            onChange={(e) => { setSelectedPlanId(e.target.value); setCreatePlanId(e.target.value); }}
            style={{ width: '100%', padding: isMobile ? '6px 8px' : '8px 10px', fontSize: isMobile ? 14 : 16, border: '1px solid #e5e7eb', borderRadius: 8 }}
          >
            <option value="">전체 플랜</option>
            {plans.map(p => (
              <option key={p.plan_id} value={p.plan_id}>{p.suggestion} (#{p.plan_id})</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <button onClick={fetchLogs} disabled={loading} style={{ height: isMobile ? 34 : 38, padding: '0 12px', borderRadius: 8, border: '1px solid #4f46e5', background: '#eef2ff', cursor: 'pointer' }}>조회</button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={onOpenCreate} style={{ height: 36, padding: '0 12px', borderRadius: 8, border: '1px solid #4f46e5', background: 'linear-gradient(180deg,#c7d2fe 0%,#a5b4fc 100%)', color: '#1e1b4b', cursor: 'pointer' }}>+ 기록 추가</button>
        </div>
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto', overflowY: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
          {isMobile && (
            <colgroup>
              {/* 액션 */}
              <col style={{ width: 72 }} />
              {/* 날짜 */}
              <col style={{ width: 70 }} />
              {/* 상태 */}
              <col style={{ width: 40 }} />
              {/* 수량 */}
              <col style={{ width: 56 }} />
              {/* 만족 */}
              <col style={{ width: 48 }} />
              {/* 메모: 남은 폭 모두 사용 */}
              <col />
            </colgroup>
          )}
          {!isMobile && (
            <colgroup>
              {/* 액션 */}
              <col style={{ width: 84 }} />
              {/* 날짜 */}
              <col style={{ width: 120 }} />
              {/* 수행 시각 */}
              <col style={{ width: 92 }} />
              {/* 상태 */}
              <col style={{ width: 72 }} />
              {/* 수량/단위 */}
              <col style={{ width: 116 }} />
              {/* 만족도 */}
              <col style={{ width: 80 }} />
              {/* 입력 */}
              <col style={{ width: 84 }} />
              {/* 메모: 남은 폭 사용 */}
              <col />
            </colgroup>
          )}
          <thead style={{ background: '#f9fafb' }}>
            <tr>
              <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>액션</th>
              <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>날짜</th>
              {!isMobile && (
                <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>수행 시각</th>
              )}
              <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>상태</th>
              <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>{isMobile ? '수량' : '수량/단위'}</th>
              <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>{isMobile ? '만족' : '만족도'}</th>
              {!isMobile && (
                <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>입력</th>
              )}
              <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>메모</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((l) => (
              <tr key={l.log_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: 10 }}>
                  {isMobile ? (
                    <button onClick={() => onOpenEdit(l)} aria-label="수정" title="수정" style={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', borderRadius: 999, background: '#fff', cursor: 'pointer' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                    </button>
                  ) : (
                    <button onClick={() => onOpenEdit(l)} style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>수정</button>
                  )}
                </td>
                <td style={{ padding: 10 }}>{new Date(l.log_date).toLocaleDateString()}</td>
                {!isMobile && (
                  <td style={{ padding: 10 }}>{l.performed_at ? new Date(l.performed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                )}
                <td style={{ padding: 10 }}>{statusLabel[l.completion_status] || l.completion_status}</td>
                <td style={{ padding: 10 }}>{`${l.quantity ?? '-'}`}{l.unit ? ` ${l.unit}` : ''}</td>
                <td style={{ padding: 10 }}>{l.user_rating ?? '-'}</td>
                {!isMobile && (
                  <td style={{ padding: 10 }}>{sourceLabel[l.source] || l.source || '-'}</td>
                )}
                <td style={{ padding: isMobile ? '8px 6px' : 10, width: isMobile ? 'auto' : undefined, maxWidth: isMobile ? 'none' : 'clamp(160px, 24vw, 220px)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{l.user_memo || ''}</td>
              </tr>
            ))}
            {(!loading && paged.length === 0) && (
              <tr>
                <td colSpan={8} style={{ padding: 16, textAlign: 'center', color: '#9ca3af' }}>데이터가 없습니다.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{loading ? '불러오는 중...' : `총 ${logs.length}건`}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>이전</button>
          <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center' }}>{page} / {totalPages}</div>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>다음</button>
        </div>
      </div>

      {openModal && (
        <ActionPlanLogModal
          open={openModal}
          onClose={() => { setOpenModal(false); setEditLog(null); }}
          onSubmitted={onSubmitted}
          reportId={Number(reportId)}
          plan={null}
          logForEdit={editLog}
          planIdForCreate={createPlanId ? Number(createPlanId) : undefined}
        />
      )}

      {/* 로그/계획 조회 오류 안내용 메시지 모달 */}
      <MessageModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title="알림"
        message={error}
        type="error"
      />
    </div>
  );
}
